<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import {
  adjustPointsInventory,
  createPointsProduct,
  listAdjustments,
  listPointsProducts,
  setPointsProductStatus,
  updatePointsProduct,
  uploadProductImage,
  type Adjustment,
  type PointsProduct,
  type PointsProductStatus,
} from "../api/points-admin";

type StatusFilter = "ALL" | PointsProductStatus;

const products = ref<PointsProduct[]>([]);
const selected = ref<PointsProduct | null>(null);
const adjustments = ref<Adjustment[]>([]);
const message = ref("");
const busy = ref(false);
const statusFilter = ref<StatusFilter>("ALL");
const currentPage = ref(1);
const pageSize = 10;
const imageFileInput = ref<HTMLInputElement | null>(null);
const createCard = ref<HTMLElement | null>(null);

const form = reactive<{
  name: string;
  subtitle: string;
  imageUrl: string;
  categoryCode: string;
  pointsCost: number;
  initialStock: number;
  sortOrder: number;
  status: PointsProductStatus;
}>({
  name: "",
  subtitle: "",
  imageUrl: "",
  categoryCode: "BRAND_MATERIAL",
  pointsCost: 10,
  initialStock: 0,
  sortOrder: 0,
  status: "DRAFT",
});

const stock = reactive({ delta: 0, reason: "" });

const filteredProducts = computed(() =>
  statusFilter.value === "ALL"
    ? products.value
    : products.value.filter((product) => product.status === statusFilter.value),
);

const pageCount = computed(() => Math.max(1, Math.ceil(filteredProducts.value.length / pageSize)));
const pagedProducts = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredProducts.value.slice(start, start + pageSize);
});

function resetForm(): void {
  Object.assign(form, {
    name: "",
    subtitle: "",
    imageUrl: "",
    categoryCode: "BRAND_MATERIAL",
    pointsCost: 10,
    initialStock: 0,
    sortOrder: 0,
    status: "DRAFT" as PointsProductStatus,
  });
  Object.assign(stock, { delta: 0, reason: "" });
  adjustments.value = [];
  if (imageFileInput.value) imageFileInput.value.value = "";
}

async function load(): Promise<void> {
  products.value = await listPointsProducts();
  if (selected.value) {
    selected.value = products.value.find((product) => product.id === selected.value?.id) ?? null;
  }
}

function closeEditor(): void {
  selected.value = null;
  adjustments.value = [];
  resetForm();
}

function edit(product: PointsProduct): void {
  selected.value = product;
  Object.assign(form, {
    name: product.name,
    subtitle: product.subtitle ?? "",
    imageUrl: product.imageUrl ?? "",
    categoryCode: product.categoryCode ?? "BRAND_MATERIAL",
    pointsCost: product.pointsCost,
    sortOrder: product.sortOrder,
    status: product.status,
  });
  Object.assign(stock, { delta: 0, reason: "" });
  adjustments.value = [];
  if (imageFileInput.value) imageFileInput.value.value = "";
  message.value = "";
  void nextTick(() => {
    createCard.value?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

async function run(task: () => Promise<void>): Promise<void> {
  busy.value = true;
  message.value = "";
  try {
    await task();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "操作失败";
  } finally {
    busy.value = false;
  }
}

async function save(): Promise<void> {
  await run(async () => {
    const editing = selected.value != null;
    if (selected.value) {
      await updatePointsProduct(selected.value.id, {
        name: form.name,
        subtitle: form.subtitle || null,
        imageUrl: form.imageUrl || null,
        categoryCode: form.categoryCode,
        pointsCost: form.pointsCost,
        sortOrder: form.sortOrder,
        expectedVersion: selected.value.version,
      });
    } else {
      await createPointsProduct({
        ...form,
        subtitle: form.subtitle || null,
        imageUrl: form.imageUrl || null,
      });
    }
    if (editing) {
      closeEditor();
      message.value = "积分商品已更新";
    } else {
      resetForm();
      message.value = "积分商品已创建";
    }
    await load();
  });
}

async function setStatus(product: PointsProduct, nextStatus: "ACTIVE" | "INACTIVE"): Promise<void> {
  await run(async () => {
    await setPointsProductStatus(product, nextStatus);
    await load();
    message.value = nextStatus === "ACTIVE" ? "积分商品已上架" : "积分商品已下架";
  });
}

async function adjust(): Promise<void> {
  if (!selected.value) return;
  await run(async () => {
    await adjustPointsInventory(selected.value!, stock.delta, stock.reason);
    adjustments.value = await listAdjustments(selected.value!.id);
    await load();
    const latest = products.value.find((product) => product.id === selected.value?.id);
    if (latest) edit(latest);
    message.value = "库存调整完成";
  });
}

async function upload(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  await run(async () => {
    form.imageUrl = (await uploadProductImage(file)).url;
    message.value = "图片上传完成";
  });
}

function statusText(status: PointsProductStatus): string {
  if (status === "ACTIVE") return "已上架";
  if (status === "INACTIVE") return "已下架";
  return "草稿";
}

function statusClass(status: PointsProductStatus): string {
  return `status-badge--${status.toLowerCase()}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString("zh-CN");
}

function previousPage(): void {
  currentPage.value = Math.max(1, currentPage.value - 1);
}

function nextPage(): void {
  currentPage.value = Math.min(pageCount.value, currentPage.value + 1);
}

watch(statusFilter, () => {
  currentPage.value = 1;
});

watch(pageCount, (count) => {
  currentPage.value = Math.min(currentPage.value, count);
});

onMounted(() =>
  run(async () => {
    await load();
  }),
);
</script>

<template>
  <section class="points-products-page">
    <header class="page-header points-products-header">
      <div>
        <p class="eyebrow">
          POINTS CATALOG
        </p>
        <h1>积分商品管理</h1>
        <p class="muted">
          管理会员可兑换的礼品及积分规则
        </p>
      </div>
    </header>

    <p
      v-if="message"
      class="notice panel points-message"
      role="status"
    >
      {{ message }}
    </p>

    <section class="panel points-products-card">
      <div class="points-products-toolbar">
        <div>
          <h2>商品列表</h2>
          <p class="muted">
            共 {{ filteredProducts.length }} 条记录
          </p>
        </div>
        <label class="status-filter">
          <span>状态</span>
          <select v-model="statusFilter">
            <option value="ALL">
              全部状态
            </option>
            <option value="ACTIVE">
              已上架
            </option>
            <option value="INACTIVE">
              已下架
            </option>
            <option value="DRAFT">
              草稿
            </option>
          </select>
        </label>
      </div>

      <div class="points-table-wrap">
        <table class="points-table">
          <thead>
            <tr>
              <th>商品信息</th>
              <th>所需积分</th>
              <th>剩余库存</th>
              <th>状态</th>
              <th>排序</th>
              <th class="points-table__actions">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in pagedProducts"
              :key="product.id"
            >
              <td>
                <div class="points-product-info">
                  <div class="points-product-thumb">
                    <img
                      v-if="product.imageUrl"
                      :src="product.imageUrl"
                      :alt="product.name"
                    >
                    <span
                      v-else
                      aria-hidden="true"
                    >礼</span>
                  </div>
                  <div class="points-product-copy">
                    <strong>{{ product.name }}</strong>
                    <span v-if="product.subtitle">{{ product.subtitle }}</span>
                    <small>{{ product.productNo }}</small>
                  </div>
                </div>
              </td>
              <td class="points-cost">
                {{ formatNumber(product.pointsCost) }}
              </td>
              <td :class="{ 'stock-empty': product.stockQty === 0 }">
                {{ formatNumber(product.stockQty) }}
              </td>
              <td>
                <span
                  class="status-badge"
                  :class="statusClass(product.status)"
                >
                  {{ statusText(product.status) }}
                </span>
              </td>
              <td class="sort-value">
                {{ product.sortOrder }}
              </td>
              <td class="points-table__actions">
                <div class="row-actions">
                  <button
                    class="text-action"
                    type="button"
                    @click="edit(product)"
                  >
                    编辑
                  </button>
                  <button
                    class="text-action text-action--muted"
                    type="button"
                    @click="setStatus(product, product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')"
                  >
                    {{ product.status === "ACTIVE" ? "下架" : "上架" }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="filteredProducts.length === 0">
              <td
                class="empty-state"
                colspan="6"
              >
                暂无符合条件的积分商品
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="points-products-footer">
        <span>共 {{ filteredProducts.length }} 条记录</span>
        <div class="pagination">
          <button
            type="button"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            上一页
          </button>
          <span>{{ currentPage }} / {{ pageCount }}</span>
          <button
            type="button"
            :disabled="currentPage === pageCount"
            @click="nextPage"
          >
            下一页
          </button>
        </div>
      </footer>
    </section>

    <section
      ref="createCard"
      class="panel points-create-card"
    >
      <div class="points-create-surface">
        <header class="points-create-card__header">
          <div>
            <h2 id="points-form-title">
              {{ selected ? "编辑积分商品" : "新增积分商品" }}
            </h2>
            <p class="muted">
              {{ selected ? `正在编辑：${selected.name}` : "填写后保存，商品会立即显示在上方列表中。" }}
            </p>
          </div>
        </header>

        <form
          class="points-form points-form--inline"
          @submit.prevent="save"
        >
          <label>
            商品名称
            <input
              v-model="form.name"
              required
              placeholder="请输入商品名称"
            >
          </label>
          <label>
            副标题
            <input
              v-model="form.subtitle"
              placeholder="选填"
            >
          </label>
          <label>
            商品分类
            <select v-model="form.categoryCode">
              <option value="BRAND_MATERIAL">
                品牌物料
              </option>
              <option value="OFFICE">
                办公用品
              </option>
              <option value="LIFESTYLE">
                生活周边
              </option>
            </select>
          </label>
          <label>
            兑换积分
            <input
              v-model.number="form.pointsCost"
              type="number"
              min="1"
              required
            >
          </label>
          <label v-if="!selected">
            初始库存
            <input
              v-model.number="form.initialStock"
              type="number"
              min="0"
              required
            >
          </label>
          <label>
            排序
            <input
              v-model.number="form.sortOrder"
              type="number"
              required
            >
          </label>
          <label v-if="!selected">
            初始状态
            <select v-model="form.status">
              <option value="DRAFT">
                草稿
              </option>
              <option value="ACTIVE">
                已上架
              </option>
              <option value="INACTIVE">
                已下架
              </option>
            </select>
          </label>
          <div
            v-else
            class="points-form__status"
          >
            <span>当前状态</span>
            <span
              class="status-badge"
              :class="statusClass(selected.status)"
            >
              {{ statusText(selected.status) }}
            </span>
          </div>
          <label class="points-form__full">
            商品图片
            <input
              v-model="form.imageUrl"
              type="url"
              placeholder="粘贴图片 URL，或选择文件上传"
            >
            <span class="upload-row">
              <input
                ref="imageFileInput"
                type="file"
                accept="image/*"
                @change="upload"
              >
              <span class="muted">支持现有商品图片上传</span>
            </span>
          </label>

          <div class="points-form__actions">
            <button
              v-if="selected"
              class="secondary-action"
              type="button"
              :disabled="busy"
              @click="closeEditor"
            >
              取消编辑
            </button>
            <button
              class="primary-action"
              type="submit"
              :disabled="busy"
            >
              {{ busy ? "保存中…" : selected ? "保存修改" : "保存" }}
            </button>
          </div>
        </form>

        <div
          v-if="selected"
          class="inventory-adjustment"
        >
          <div>
            <h3>库存调整</h3>
            <p class="muted">
              当前库存 {{ formatNumber(selected.stockQty) }}，所有调整继续记录库存流水。
            </p>
          </div>
          <form
            class="inventory-adjustment__form"
            @submit.prevent="adjust"
          >
            <label>
              调整数量
              <input
                v-model.number="stock.delta"
                type="number"
                required
              >
            </label>
            <label>
              调整原因
              <input
                v-model="stock.reason"
                required
                placeholder="例如：补货"
              >
            </label>
            <button
              class="secondary-action"
              type="submit"
              :disabled="busy"
            >
              确认调整
            </button>
          </form>
        </div>
      </div>
    </section>
  </section>
</template>

<style scoped>
.points-products-page {
  display: grid;
  gap: 24px;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
}

.points-products-header {
  align-items: start;
}

.points-products-header h1 {
  margin-bottom: 8px;
  font-size: clamp(24px, 3vw, 30px);
  letter-spacing: -0.035em;
}

.eyebrow {
  margin: 0 0 8px;
  color: var(--admin-primary-deep);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.primary-action,
.secondary-action,
.text-action {
  border: 0;
  font: inherit;
}

.primary-action {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 9px;
  background: var(--admin-primary);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(36, 138, 85, 0.16);
}

.primary-action:hover {
  background: var(--admin-primary-deep);
}

.points-message {
  margin: 0;
  padding: 12px 16px;
  font-size: 13px;
}

.points-products-card {
  min-width: 0;
  padding: 0;
  overflow: hidden;
}

.points-create-card {
  min-width: 0;
  padding: 0;
  overflow: hidden;
}

.points-create-surface {
  width: 100%;
}

.points-create-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 28px 0;
}

.points-create-card__header h2 {
  margin: 0 0 6px;
  font-size: 18px;
}

.points-create-card__header p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.points-products-toolbar,
.points-products-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
}

.points-products-toolbar {
  border-bottom: 1px solid var(--admin-outline-soft);
}

.points-products-toolbar h2 {
  margin: 0 0 4px;
  font-size: 17px;
}

.points-products-toolbar p,
.points-products-footer {
  margin: 0;
  font-size: 12px;
}

.status-filter {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
}

.status-filter select {
  min-width: 116px;
  padding: 8px 30px 8px 10px;
  border: 1px solid var(--admin-outline);
  border-radius: 8px;
  background: var(--admin-surface);
  color: var(--admin-ink);
}

.points-table-wrap {
  overflow-x: auto;
}

.points-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  text-align: left;
}

.points-table th,
.points-table td {
  padding: 14px 22px;
  border-bottom: 1px solid var(--admin-outline-soft);
  vertical-align: middle;
}

.points-table th {
  background: #f8faf9;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.points-table td {
  color: var(--admin-ink);
  font-size: 13px;
}

.points-product-info {
  display: flex;
  min-width: 250px;
  align-items: center;
  gap: 12px;
}

.points-product-thumb {
  display: grid;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  place-items: center;
  overflow: hidden;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 9px;
  background: var(--admin-surface-low);
  color: var(--admin-primary-deep);
  font-weight: 700;
}

.points-product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.points-product-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.points-product-copy strong {
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.points-product-copy span,
.points-product-copy small {
  overflow: hidden;
  color: var(--admin-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.points-product-copy small {
  color: #7b857d;
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 11px;
}

.points-cost {
  color: var(--admin-primary-deep) !important;
  font-size: 15px !important;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
}

.stock-empty {
  color: #ba1a1a !important;
  font-weight: 700;
}

.sort-value {
  color: var(--admin-muted) !important;
  font-variant-numeric: tabular-nums;
}

.points-table__actions {
  text-align: right !important;
  white-space: nowrap;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}

.text-action {
  padding: 4px 0;
  background: transparent;
  color: var(--admin-primary-deep);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.text-action--muted {
  color: var(--admin-muted);
  font-weight: 400;
}

.text-action:hover {
  color: var(--admin-primary);
}

.status-badge {
  display: inline-flex;
  min-width: 58px;
  align-items: center;
  justify-content: center;
  padding: 5px 9px;
  border: 1px solid transparent;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.status-badge--active {
  border-color: rgba(60, 179, 113, 0.22);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.status-badge--inactive {
  border-color: var(--admin-outline-soft);
  background: #f2f3f2;
  color: var(--admin-muted);
}

.status-badge--draft {
  border-color: rgba(191, 129, 0, 0.2);
  background: #fff8e8;
  color: #8a6200;
}

.empty-state {
  padding: 42px 22px !important;
  color: var(--admin-muted) !important;
  text-align: center;
}

.points-products-footer {
  border-top: 1px solid var(--admin-outline-soft);
  color: var(--admin-muted);
}

.pagination {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pagination button {
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid var(--admin-outline);
  border-radius: 7px;
  background: var(--admin-surface);
  color: var(--admin-muted);
  font-size: 12px;
}

.pagination button:not(:disabled):hover {
  border-color: var(--admin-primary);
  color: var(--admin-primary-deep);
}

.pagination span {
  min-width: 42px;
  color: var(--admin-muted);
  font-size: 12px;
  text-align: center;
}

.points-form {
  display: grid;
  gap: 16px;
  padding: 24px 28px 30px;
}

.points-form--inline {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.points-form label,
.inventory-adjustment__form label {
  display: grid;
  gap: 7px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.points-form input,
.points-form select,
.inventory-adjustment__form input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--admin-outline);
  border-radius: 8px;
  background: var(--admin-surface);
  color: var(--admin-ink);
  font-size: 13px;
  font-weight: 400;
}

.points-form__full {
  grid-column: 1 / -1;
}

.points-form__status {
  display: grid;
  align-content: start;
  gap: 7px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 400;
}

.upload-row input {
  padding: 0;
  border: 0;
}

.inventory-adjustment {
  display: grid;
  gap: 16px;
  margin-top: 4px;
  margin-right: 28px;
  margin-bottom: 30px;
  margin-left: 28px;
  padding: 16px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 10px;
  background: #f8faf9;
}

.inventory-adjustment h3 {
  margin: 0 0 5px;
  font-size: 14px;
}

.inventory-adjustment p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.inventory-adjustment__form {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto;
  align-items: end;
  gap: 10px;
}

.secondary-action {
  min-height: 40px;
  padding: 9px 15px;
  border: 1px solid rgba(36, 138, 85, 0.28);
  border-radius: 8px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 13px;
  font-weight: 700;
}

.secondary-action:hover {
  background: #e2f5e9;
}

.points-form__actions {
  display: flex;
  justify-content: end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--admin-outline-soft);
}

.points-form--inline .points-form__actions {
  grid-column: 1 / -1;
}

.points-form__actions .primary-action {
  min-width: 116px;
}

button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid rgba(60, 179, 113, 0.25);
  outline-offset: 2px;
}

@media (max-width: 720px) {
  .points-products-header {
    align-items: stretch;
    flex-direction: column;
  }

  .points-products-toolbar,
  .points-products-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .status-filter {
    justify-content: space-between;
  }

  .status-filter select {
    flex: 1;
  }

  .points-form {
    padding-right: 20px;
    padding-left: 20px;
  }

  .points-form--inline {
    grid-template-columns: 1fr;
  }

  .inventory-adjustment__form {
    grid-template-columns: 1fr 1fr;
  }

  .inventory-adjustment__form .secondary-action {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .points-products-page {
    width: min(100% - 32px, 720px);
  }
}

@media (max-width: 640px) {
  .points-products-page {
    width: min(100% - 24px, 560px);
  }
}
</style>
